"use client";

import { useEffect, useMemo, useState } from "react";
import { captureException } from "@sentry/nextjs";
import dynamic from "next/dynamic";
import Spinner from "components/utilities/Spinner";
import { getSortTermLocalStorage, setSortTermLocalStorage } from "utils/expirableLocalStorage";
import { isRecoverableChunkError } from "utils/recoverableChunkError";
const ErrorContent = dynamic<{
  errorDetail: string;
  sentry_error_id: string;
}>(() => import("./src/500/ErrorContent"), {
  loading: () => <div className='flex items-center justify-center p-4'>
      <Spinner />
    </div>
});
const UNKNOWN_ERROR_MESSAGE = "unknown error";
type ErrorWithDigest = Error & {
  digest?: string;
};
const safeReadErrorProp = <K extends keyof ErrorWithDigest,>(error: ErrorWithDigest, key: K): ErrorWithDigest[K] | undefined => {
  try {
    return error[key];
  } catch {
    return undefined;
  }
};
const getReadableErrorMessage = (error?: ErrorWithDigest) => {
  if (!error) return UNKNOWN_ERROR_MESSAGE;
  return error.message || error.digest || error.name || UNKNOWN_ERROR_MESSAGE;
};
const getErrorDetails = (error?: ErrorWithDigest) => {
  if (!error) return "NA";
  const details = {
    message: safeReadErrorProp(error, "message") || UNKNOWN_ERROR_MESSAGE,
    digest: safeReadErrorProp(error, "digest"),
    name: safeReadErrorProp(error, "name"),
    stack: safeReadErrorProp(error, "stack")
  };
  try {
    return JSON.stringify(details);
  } catch {
    return details.message || getReadableErrorMessage(error);
  }
};
export default function GlobalError({
  error
}: {
  error: ErrorWithDigest;
}) {
  const [errorId, setErrorId] = useState<string | null>(null);
  const errorDetails = useMemo(() => getErrorDetails(error), [error]);
  useEffect(() => {
    if (!error) {
      return;
    }
    const sendToSentry = (exception: Error & {
      digest?: string;
    }, extra?: Record<string, unknown>) => {
      const eventId = captureException(exception, {
        tags: {
          ws_location: "Global"
        },
        extra: {
          readableMessage: getReadableErrorMessage(exception),
          errorDetail: getErrorDetails(exception),
          ...extra
        }
      });
      if (typeof eventId === "string") {
        setErrorId(eventId);
      } else {
        setErrorId(null);
      }
    };
    try {
      // A missing/mismatched webpack chunk (stale build after a deploy) shows
      // up either as a `ChunkLoadError` or as the webpack runtime's
      // `TypeError: Cannot read properties of undefined (reading 'call')`.
      // Both are usually fixed by a single reload onto the fresh build.
      if (isRecoverableChunkError(error)) {
        const isRepeated = getSortTermLocalStorage("isChunkLoadError");
        if (isRepeated) {
          sendToSentry(error, {
            ChunkLoadError: "repeated"
          });
          return;
        }
        setSortTermLocalStorage("isChunkLoadError", "true", 300);
        setTimeout(() => {
          window.location.reload();
        }, 200);
        return;
      }
      sendToSentry(error);
    } catch (captureError) {
      // Avoid throwing from the error boundary itself.
      console.error("Failed to record global error", captureError);
      setErrorId(null);
    }
  }, [error]);
  return <html lang='en' data-sentry-component="GlobalError" data-sentry-source-file="global-error.tsx">
      <body>
        {errorId ? <ErrorContent errorDetail={errorDetails} sentry_error_id={errorId} /> : <div className='flex items-center justify-center p-4'>
            <Spinner />
          </div>}
      </body>
    </html>;
}